// API base URL configuration
// Always use same origin (proxied through Pages Functions) for cookie support
// The actual backend URL is configured in functions/api/[[path]].ts
const API_BASE_URL = '';  // Empty = same origin, uses /api/* proxy

// Clean up any old localStorage values that might cause issues
if (typeof window !== 'undefined') {
  const oldKey = 'tirker_api_url';
  if (localStorage.getItem(oldKey)) {
    console.log('Cleaning up legacy API URL setting...');
    localStorage.removeItem(oldKey);
  }
}

/**
 * Get the API base URL (always empty for proxy)
 */
export function getApiUrl(): string {
  return API_BASE_URL;
}

class ApiClient {
  private baseUrl: string;

  constructor(baseUrl: string) {
    this.baseUrl = baseUrl;
  }

  setBaseUrl(url: string): void {
    this.baseUrl = url;
  }

  getBaseUrl(): string {
    return this.baseUrl;
  }

  private async request<T>(
    method: string,
    path: string,
    data?: unknown
  ): Promise<T> {
    // Add cache-busting timestamp to prevent ANY caching
    const separator = path.includes('?') ? '&' : '?';
    const url = `${this.baseUrl}/api${path}${separator}_t=${Date.now()}`;

    const headers: HeadersInit = {
      'Content-Type': 'application/json',
    };

    const options: RequestInit = {
      method,
      headers,
      credentials: 'include', // Include cookies for session auth
      cache: 'no-store',      // Disable browser caching for fresh data
    };

    if (data) {
      options.body = JSON.stringify(data);
    }

    const response = await fetch(url, options);

    if (!response.ok) {
      const error = await response.json().catch(() => ({})) as { error?: { message?: string } };
      throw new Error(error.error?.message || `Request failed: ${response.status}`);
    }

    return response.json();
  }

  get<T>(path: string): Promise<T> {
    return this.request<T>('GET', path);
  }

  post<T>(path: string, data?: unknown): Promise<T> {
    return this.request<T>('POST', path, data);
  }

  patch<T>(path: string, data?: unknown): Promise<T> {
    return this.request<T>('PATCH', path, data);
  }

  put<T>(path: string, data?: unknown): Promise<T> {
    return this.request<T>('PUT', path, data);
  }

  delete<T>(path: string): Promise<T> {
    return this.request<T>('DELETE', path);
  }

  async upload(
    path: string,
    fileOrFormData: File | FormData,
    onProgress?: (progress: number) => void
  ): Promise<any> {
    const url = `${this.baseUrl}/api${path}`;

    // Ensure we have FormData
    let formData: FormData;
    if (fileOrFormData instanceof File) {
      formData = new FormData();
      formData.append('file', fileOrFormData);
    } else {
      formData = fileOrFormData;
    }

    return new Promise((resolve, reject) => {
      const xhr = new XMLHttpRequest();

      xhr.upload.addEventListener('progress', (e) => {
        if (e.lengthComputable && onProgress) {
          const progress = Math.round((e.loaded / e.total) * 100);
          onProgress(progress);
        }
      });

      xhr.addEventListener('load', () => {
        if (xhr.status >= 200 && xhr.status < 300) {
          try {
            resolve(JSON.parse(xhr.responseText));
          } catch {
            resolve({ success: true });
          }
        } else {
          try {
            const error = JSON.parse(xhr.responseText);
            reject(new Error(error.error?.message || `Upload failed: ${xhr.status}`));
          } catch {
            reject(new Error(`Upload failed: ${xhr.status}`));
          }
        }
      });

      xhr.addEventListener('error', () => {
        reject(new Error('Network error during upload'));
      });

      xhr.open('POST', url);
      xhr.withCredentials = true; // Include cookies
      // Don't set Content-Type - browser will set it with correct boundary for FormData
      xhr.send(formData);
    });
  }
}

export const api = new ApiClient(API_BASE_URL);
